import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Veiculo } from '../../types';
import { veiculosService } from '../../services/veiculos';
import CurrencyInput from '../../components/CurrencyInput';
import { KmInput, LitrosInput, PlacaInput, DecimalInput } from '../../components/MaskedInputs';
import SelecaoDeMarcaEModelo from '../../components/SelecaoDeMarcaEModelo';
import { usePermissoes } from '../../hooks/usePermissoes';

/**
 * Cores de veiculo, lista fixa.
 *
 * Nao ha cadastro de cor nova de proposito: o conjunto e conhecido e estavel,
 * e texto livre aqui produzia o mesmo problema que a marca tinha — "Prata",
 * "prata" e "PRATA" viravam tres valores e nenhum filtro confiavel.
 */
const CORES = [
  'Amarelo', 'Azul', 'Bege', 'Branco', 'Cinza', 'Dourado', 'Laranja',
  'Marrom', 'Prata', 'Preto', 'Rosa', 'Roxo', 'Verde', 'Vermelho', 'Vinho',
];

const VeiculosForm: React.FC = () => {
  const { pode } = usePermissoes();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    placa: '',
    marca_id: '',
    modelo_id: '',
    ano_fabricacao: '',
    ano_modelo: '',
    cor: '',
    combustivel: '',
    chassi: '',
    renavam: '',
    quilometragem: '',
    capacidade_tanque: '',
    consumo_medio: '',
    status: 'ativo',
    categoria: 'passeio',
    valor_compra: '',
    valor_fipe: '',
    data_compra: '',
    vencimento_licenciamento: '',
    vencimento_seguro: '',
    seguradora: '',
    apolice_seguro: '',
    observacoes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Buscar dados do veículo se estiver editando
  const { data: veiculoData, isLoading: isLoadingVeiculo } = useQuery({
    queryKey: ['veiculo', id],
    queryFn: () => veiculosService.getVeiculo(Number(id)),
    enabled: isEditing,
  });

  // Mutation para criar/atualizar veículo
  const mutation = useMutation({
    mutationFn: (data: Partial<Veiculo>) => {
      if (isEditing) {
        return veiculosService.updateVeiculo(Number(id), data);
      }
      return veiculosService.createVeiculo(data);
    },
    onSuccess: (response) => {
      if (response.success) {
        toast.success(
          isEditing 
            ? 'Veículo atualizado com sucesso!' 
            : 'Veículo criado com sucesso!'
        );
        // Invalidar so a lista deixava o detalhe e o proprio formulario
        // servindo o cache anterior a edicao: o registro aparecia atualizado na
        // listagem e desatualizado ao abrir. Sao chaves diferentes, e as duas
        // precisam cair.
        queryClient.invalidateQueries({ queryKey: ['veiculos'] });
        if (id) {
          queryClient.invalidateQueries({ queryKey: ['veiculo', id] });
        }
        navigate('/veiculos');
      } else {
        toast.error(response.message || 'Erro ao salvar veículo');
      }
    },
    onError: (error: any) => {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        toast.error('Por favor, corrija os erros no formulário');
      } else {
        toast.error('Erro ao salvar veículo');
      }
    }
  });

  // Carregar dados do veículo quando estiver editando
  useEffect(() => {
    if (veiculoData?.data && isEditing) {
      const veiculo = veiculoData.data;
      setFormData({
        placa: veiculo.placa || '',
        // Chegam JUNTOS num unico setFormData. E por isso que a limpeza
        // automatica do modelo vive no evento de troca da marca, e nao
        // num efeito: aqui ela apagaria o modelo do veiculo em edicao.
        marca_id: veiculo.marca_id?.toString() || '',
        modelo_id: veiculo.modelo_id?.toString() || '',
        ano_fabricacao: veiculo.ano_fabricacao?.toString() || '',
        ano_modelo: veiculo.ano_modelo?.toString() || '',
        cor: veiculo.cor || '',
        combustivel: veiculo.combustivel || '',
        chassi: veiculo.chassi || '',
        renavam: veiculo.renavam || '',
        quilometragem: veiculo.quilometragem?.toString() || '',
        capacidade_tanque: veiculo.capacidade_tanque?.toString() || '',
        consumo_medio: veiculo.consumo_medio?.toString() ?? '',
        status: veiculo.status || 'ativo',
        categoria: veiculo.categoria || 'passeio',
        valor_compra: veiculo.valor_compra?.toString() ?? '',
        valor_fipe: veiculo.valor_fipe?.toString() ?? '',
        data_compra: veiculo.data_compra ?? '',
        vencimento_licenciamento: veiculo.vencimento_licenciamento ?? '',
        vencimento_seguro: veiculo.vencimento_seguro ?? '',
        seguradora: veiculo.seguradora ?? '',
        apolice_seguro: veiculo.apolice_seguro ?? '',
        observacoes: veiculo.observacoes ?? ''
      });
    }
  }, [veiculoData, isEditing]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação básica no frontend
    const newErrors: Record<string, string> = {};
    
    if (!formData.placa) newErrors.placa = 'Placa é obrigatória';
    if (!formData.marca_id) newErrors.marca_id = 'Marca é obrigatória';
    if (!formData.modelo_id) newErrors.modelo_id = 'Modelo é obrigatório';
    if (!formData.ano_fabricacao) newErrors.ano_fabricacao = 'Ano de fabricação é obrigatório';
    if (!formData.ano_modelo) newErrors.ano_modelo = 'Ano do modelo é obrigatório';
    if (!formData.cor) newErrors.cor = 'Cor é obrigatória';
    if (!formData.combustivel) newErrors.combustivel = 'Combustível é obrigatório';
    if (!formData.chassi) newErrors.chassi = 'Chassi é obrigatório';
    if (!formData.renavam) newErrors.renavam = 'Renavam é obrigatório';
    if (!formData.quilometragem) newErrors.quilometragem = 'Quilometragem é obrigatória';
    if (!formData.capacidade_tanque) newErrors.capacidade_tanque = 'Capacidade do tanque é obrigatória';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    // Converter dados para o formato esperado pela API
    const submitData: Partial<Veiculo> = {
      ...formData,
      ano_fabricacao: parseInt(formData.ano_fabricacao),
      marca_id: parseInt(formData.marca_id),
      modelo_id: parseInt(formData.modelo_id),
      ano_modelo: parseInt(formData.ano_modelo),
      quilometragem: parseFloat(formData.quilometragem),
      capacidade_tanque: parseFloat(formData.capacidade_tanque),
      consumo_medio: formData.consumo_medio ? parseFloat(formData.consumo_medio) : null,
      valor_compra: formData.valor_compra ? parseFloat(formData.valor_compra) : null,
      valor_fipe: formData.valor_fipe ? parseFloat(formData.valor_fipe) : null,
      status: formData.status as 'ativo' | 'inativo' | 'manutencao' | 'vendido',
      categoria: formData.categoria as 'passeio' | 'utilitario' | 'comercial' | 'motocicleta' | 'caminhao',
    };

    mutation.mutate(submitData);
  };

  const getCurrentYear = () => new Date().getFullYear();

  if (isLoadingVeiculo) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Editar veículo' : 'Novo veículo'}
        </h1>
        <p className="text-gray-600 mt-2">
          {isEditing ? 'Atualize as informações do veículo' : 'Cadastre um novo veículo na frota'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Informações Básicas */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">Informações Básicas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <PlacaInput
                name="placa"
                value={formData.placa}
                onChange={handleInputChange}
                label="Placa"
                required
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.placa ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.placa && <p className="text-red-500 text-sm mt-1">{errors.placa}</p>}
            </div>

            <SelecaoDeMarcaEModelo
              marcaId={formData.marca_id}
              modeloId={formData.modelo_id}
              onChange={(campos) => setFormData((prev) => ({ ...prev, ...campos }))}
              podeCadastrar={pode('veiculos', 'criar')}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ano de Fabricação *
              </label>
              <input
                type="number"
                name="ano_fabricacao"
                value={formData.ano_fabricacao}
                onChange={handleInputChange}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.ano_fabricacao ? 'border-red-500' : 'border-gray-300'
                }`}
                min="1900"
                max={getCurrentYear() + 1}
              />
              {errors.ano_fabricacao && <p className="text-red-500 text-sm mt-1">{errors.ano_fabricacao}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ano do Modelo *
              </label>
              <input
                type="number"
                name="ano_modelo"
                value={formData.ano_modelo}
                onChange={handleInputChange}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.ano_modelo ? 'border-red-500' : 'border-gray-300'
                }`}
                min="1900"
                max={getCurrentYear() + 1}
              />
              {errors.ano_modelo && <p className="text-red-500 text-sm mt-1">{errors.ano_modelo}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cor *
              </label>
              <select
                name="cor"
                value={formData.cor}
                onChange={handleInputChange}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.cor ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Selecione a cor</option>
                {/* Um veiculo cadastrado antes desta lista pode ter cor fora
                    dela. Ela entra como opcao para o registro nao perder a cor
                    ao ser editado — sem isso, abrir e salvar trocaria a cor
                    dele em silencio. */}
                {formData.cor && !CORES.includes(formData.cor) && (
                  <option value={formData.cor}>{formData.cor}</option>
                )}
                {CORES.map((cor) => (
                  <option key={cor} value={cor}>{cor}</option>
                ))}
              </select>
              {errors.cor && <p className="text-red-500 text-sm mt-1">{errors.cor}</p>}
            </div>
          </div>
        </div>

        {/* Informações Técnicas */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">Informações Técnicas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Combustível *
              </label>
              <select
                name="combustivel"
                value={formData.combustivel}
                onChange={handleInputChange}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.combustivel ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Selecione...</option>
                <option value="Flex">Flex</option>
                <option value="Gasolina">Gasolina</option>
                <option value="Etanol">Etanol</option>
                <option value="Diesel">Diesel</option>
                <option value="GNV">GNV</option>
                <option value="Elétrico">Elétrico</option>
                <option value="Híbrido">Híbrido</option>
              </select>
              {errors.combustivel && <p className="text-red-500 text-sm mt-1">{errors.combustivel}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chassi *
              </label>
              <input
                type="text"
                name="chassi"
                value={formData.chassi}
                onChange={handleInputChange}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.chassi ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="17 caracteres"
                maxLength={17}
              />
              {errors.chassi && <p className="text-red-500 text-sm mt-1">{errors.chassi}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Renavam *
              </label>
              <input
                type="text"
                name="renavam"
                value={formData.renavam}
                onChange={handleInputChange}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.renavam ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="11 dígitos"
                maxLength={11}
              />
              {errors.renavam && <p className="text-red-500 text-sm mt-1">{errors.renavam}</p>}
            </div>

            <div>
              <KmInput
                name="quilometragem"
                value={formData.quilometragem}
                onChange={handleInputChange}
                label="Quilometragem (km)"
                required
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.quilometragem ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.quilometragem && <p className="text-red-500 text-sm mt-1">{errors.quilometragem}</p>}
              {/* Na edicao, mudar o km e corrigir o painel: o servidor passa a
                  usar este valor como o atual, mesmo que seja menor. */}
              {isEditing && (
                <p className="text-xs text-gray-500 mt-1">
                  Ao alterar, este passa a ser o km atual do veículo, mesmo que seja menor. Registros anteriores
                  com km maior deixam de contar.
                </p>
              )}
            </div>

            <div>
              <LitrosInput
                name="capacidade_tanque"
                value={formData.capacidade_tanque}
                onChange={handleInputChange}
                label="Capacidade do Tanque (L)"
                required
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.capacidade_tanque ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.capacidade_tanque && <p className="text-red-500 text-sm mt-1">{errors.capacidade_tanque}</p>}
            </div>

            <div>
              <DecimalInput
                name="consumo_medio"
                value={formData.consumo_medio}
                onChange={handleInputChange}
                label="Consumo Médio (km/L)"
                sufixo=" km/L"
                casas={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {errors.consumo_medio && <p className="text-red-500 text-sm mt-1">{errors.consumo_medio}</p>}
            </div>
          </div>
        </div>

        {/* Status e Categoria */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">Status e categoria</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="manutencao">Manutenção</option>
                <option value="vendido">Vendido</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria *
              </label>
              <select
                name="categoria"
                value={formData.categoria}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="passeio">Passeio</option>
                <option value="utilitario">Utilitário</option>
                <option value="comercial">Comercial</option>
                <option value="motocicleta">Motocicleta</option>
                <option value="caminhao">Caminhão</option>
              </select>
            </div>
          </div>
        </div>

        {/* Informações Financeiras */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">Informações Financeiras</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CurrencyInput
              name="valor_compra"
              value={formData.valor_compra}
              onChange={handleInputChange}
              label="Valor de Compra (R$)"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <CurrencyInput
              name="valor_fipe"
              value={formData.valor_fipe}
              onChange={handleInputChange}
              label="Valor FIPE (R$)"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de Compra
              </label>
              <input
                type="date"
                name="data_compra"
                value={formData.data_compra}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Documentos */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">Documentos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vencimento do Licenciamento
              </label>
              <input
                type="date"
                name="vencimento_licenciamento"
                value={formData.vencimento_licenciamento}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vencimento do Seguro
              </label>
              <input
                type="date"
                name="vencimento_seguro"
                value={formData.vencimento_seguro}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seguradora
              </label>
              <input
                type="text"
                name="seguradora"
                value={formData.seguradora}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nome da seguradora"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apólice do Seguro
              </label>
              <input
                type="text"
                name="apolice_seguro"
                value={formData.apolice_seguro}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Número da apólice"
              />
            </div>
          </div>
        </div>

        {/* Observações */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">Observações</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações Adicionais
            </label>
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleInputChange}
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Informações adicionais sobre o veículo..."
            />
          </div>
        </div>

        {/* Botões */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/veiculos')}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Salvando...
              </div>
            ) : (
              isEditing ? 'Atualizar Veículo' : 'Criar veículo'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VeiculosForm;
